// @ts-check

/**
 * @file Renders the user roles route for a user.
 */

import {useLocalSearchParams} from "expo-router"
import gettextConfig from "gettext-universal/build/src/config.js"
import _ from "gettext-universal/build/src/translate.js"
import useLocale from "gettext-universal/build/src/use-locale-expo.js"
import {memo, useEffect} from "react"
import {ShapeComponent, shapeComponent} from "set-state-compare/build/shape-component.js"
import {Text, View} from "react-native-propforge"
import Button from "../../../src/components/button"
import Modal from "../../../src/components/modal"
import {User} from "../../../src/frontend-models/user"
import {UserRole} from "../../../src/frontend-models/user-role"
import errorMessage from "../../../src/shared/error-message"
import {requiredRouteParam} from "../../../src/shared/route-params"
import {routePathWithParams} from "../../../src/screens/user-roles/shared"
import UserNavBar from "../../../src/screens/user-roles/user-nav-bar"
import UserRoleRow from "../../../src/screens/user-roles/user-role-row"
import {useScreenLoading} from "../../../src/components/board-layout/screen-loading"
import ScreenTitle from "../../../src/components/screen-title"
import useThemeColors, {useThemeName} from "../../../src/shared/use-theme-colors"

/** @import {NamedExoticComponent, ReactNode} from "react" */
/** @type {Record<string, object>} */
const styles = {}

/**
 * Normalizes a locale for user-role labels.
 * @param {string | null | undefined} locale Locale candidate.
 * @returns {"da" | "de" | "en"} Locale accepted by role formatting.
 */
function normalizeSupportedLocale(locale) {
  if (typeof locale === "string") {
    const normalizedLocale = locale.toLowerCase()
    if (normalizedLocale.startsWith("da")) return "da"
    if (normalizedLocale.startsWith("de")) return "de"
  }
  return "en"
}

/** @typedef {{[key: string]: unknown}} UserRolesIndexScreenProps */

/**
 * @typedef {object} UserRolesIndexScreenState
 * @property {string | null} confirmingDeleteId User role id awaiting delete confirmation.
 * @property {boolean} loading Whether user roles are loading.
 * @property {{kind: "error" | "success", text: string} | null} status Screen status message.
 * @property {User | null} user Loaded user record.
 * @property {UserRole[]} userRoles Loaded user roles.
 */

/**
 * User roles list screen migrated from the legacy Rails/React app.
 * @augments {ShapeComponent<UserRolesIndexScreenProps, UserRolesIndexScreenState>}
 */
class UserRolesIndexScreen extends ShapeComponent {
  /** @type {ReturnType<typeof useLocalSearchParams>} */
  params
  /** @type {"da" | "de" | "en"} */
  locale
  /** @type {ReturnType<typeof useThemeColors>} */
  colors = this.hookField()
  /** @type {ReturnType<typeof useThemeName>} */
  themeName = this.hookField()
  /** @type {string} */
  userID

  /** @type {UserRolesIndexScreenState} */
  state = {
    confirmingDeleteId: null,
    loading: true,
    status: null,
    user: null,
    userRoles: []
  }
  loadRequestId = 0

  /** Initializes route params and user-role loading. */
  setup() {
    useLocale()
    useScreenLoading(this.s.loading)
    const params =
      /** @type {Record<string, string | string[]>} */ useLocalSearchParams()
    const locale = normalizeSupportedLocale(gettextConfig.getLocale())
    const userID = requiredRouteParam(params, "id", "User ID")
    this.params = params
    this.locale = locale
    this.userID = userID
    this.colors = useThemeColors()
    this.themeName = useThemeName()
    useEffect(() => {
      void this.loadUserRoles()
    }, [params.deleted, params.saved, userID])
  }

  /**
   * Renders user roles for the selected user.
   * @returns {ReactNode} User roles screen content.
   */
  render() {
    const {confirmingDeleteId, loading, status, user, userRoles} = this.s
    const colors = this.tt.colors
    const themeName = this.tt.themeName
    const screenTitle = _("Roles for %{name}", {
      name: user?.name() || ""
    })
    if (loading) {
      return (
        <View
          style={styles.loadingScreen ||= {flex: 1}}
          testID="userRolesScreen"
        />
      )
    }
    return (
      <View
        style={styles.screen ||= {
          flex: 1
        }}
        testID="userRolesScreen"
      >
        <ScreenTitle>{screenTitle}</ScreenTitle>
        <Text
          style={styles.title ||= {
            color: "#111827",
            fontSize: 24,
            fontWeight: 700,
            marginBottom: 16
          }}
          testID="userRolesScreen/title"
        >
          {screenTitle}
        </Text>
        {user &&
          <UserNavBar
            active="userRoles"
            locale={this.tt.locale}
            params={this.tt.params}
            userId={user.id()}
          />
        }
        {status &&
          <Text
            style={styles[`status-${status.kind}`] ||= {
              color: status.kind === "error" ? "#b91c1c" : "#047857",
              fontSize: 14,
              fontWeight: 600,
              marginBottom: 16
            }}
            testID="userRolesScreen/status"
          >
            {status.text}
          </Text>
        }
        {user &&
          <View
            style={styles.newButtonRow ||= {
              alignSelf: "flex-start",
              marginBottom: 16
            }}
            testID="userRolesScreen/newButtonRow"
          >
            <Button
              href={routePathWithParams(this.tt.params, `/user-roles/new?user_id=${encodeURIComponent(String(user.id()))}`)}
              iconName="plus"
              label={_("Add new user role")}
              testID="userRolesScreen/newButton"
              variant="neutral"
            />
          </View>
        }
        <Modal
          cardStyle={styles[`confirmModalCard-${themeName}`] ||= {
            borderColor: colors.danger,
            maxWidth: 420
          }}
          isVisible={Boolean(confirmingDeleteId)}
          onRequestClose={this.tt.onCancelDeletePress}
          testID="userRolesScreen/deleteConfirm"
        >
          <Text
            style={styles[`confirmTitle-${themeName}`] ||= {
              color: colors.dangerText,
              fontSize: 16,
              fontWeight: 700,
              marginBottom: 12
            }}
            testID="userRolesScreen/deleteConfirm/title"
          >
            {_("Delete user role")}
          </Text>
          <View
            style={styles.confirmActions ||= {
              flexDirection: "row",
              gap: 8
            }}
            testID="userRolesScreen/deleteConfirm/actions"
          >
            <Button
              iconName="xmark"
              label={_("Cancel")}
              onPress={this.tt.onCancelDeletePress}
              testID="userRolesScreen/deleteConfirm/cancelButton"
              variant="outline"
            />
            <Button
              iconName="trash"
              label={_("Delete")}
              onPress={this.tt.onConfirmDeletePress}
              testID="userRolesScreen/deleteConfirm/confirmButton"
              variant="destructive"
            />
          </View>
        </Modal>
        {userRoles.length === 0 &&
          <Text
            style={styles.emptyText ||= {
              color: "#4b5563",
              fontSize: 15
            }}
            testID="userRolesScreen/emptyText"
          >
            {_("The user has no roles")}
          </Text>
        }
        {userRoles.map((userRole) => (
          <UserRoleRow
            editPath={routePathWithParams(
              this.tt.params,
              `/user-roles/${userRole.id()}/edit`
            )}
            key={String(userRole.id())}
            locale={this.tt.locale}
            onDeletePress={this.tt.onDeletePress}
            role={userRole.role() || ""}
            userRoleId={String(userRole.id())}
          />
        ))}
      </View>
    )
  }

  /**
   * Loads the selected user's roles.
   * @returns {Promise<void>} Resolves when user-role loading completes.
   */
  loadUserRoles = async () => {
    const requestId = ++this.loadRequestId
    try {
      const [user, userRoles] = await Promise.all([
        User
          .select({User: ["id", "email", "firstName", "lastName", "name"]})
          .findBy({id: this.tt.userID}),
        UserRole
          .where({userId: this.tt.userID})
          .select(["id", "role", "translatedRole", "userId"])
          .order([["role", "asc"], ["id", "asc"]])
          .toArray()
      ])
      if (this.loadRequestId !== requestId) return
      if (!user) {
        throw new Error(`User not found: ${this.tt.userID}`)
      }
      /** @type {{kind: "error" | "success", text: string} | null} */
      let status = null
      if (this.tt.params.saved === "1") {
        status = {
          kind: "success",
          text: _("The user role was saved")
        }
      } else if (this.tt.params.deleted === "1") {
        status = {
          kind: "success",
          text: _("The user role was deleted")
        }
      }
      this.s.user = user
      this.s.userRoles = userRoles
      this.s.loading = false
      this.s.status = status
    } catch (error) {
      if (this.loadRequestId !== requestId) return
      const message = errorMessage(error)
      this.s.loading = false
      this.s.status = {
        kind: "error",
        text:
          message === "Forbidden"
            ? _("You do not have access to this page")
            : `${_("Could not load user roles")}: ${message}`
      }
    }
  }

  /**
   * Opens delete confirmation for a user role.
   * @param {string} userRoleID User role id to confirm.
   */
  onDeletePress = (userRoleID) => {
    this.s.confirmingDeleteId = userRoleID
    this.s.status = null
  }

  /** Closes delete confirmation. */
  onCancelDeletePress = () => {
    this.s.confirmingDeleteId = null
  }

  /**
   * Deletes the confirmed user role.
   * @returns {Promise<void>} Resolves when delete handling completes.
   * @rejects {Error} When no user role id has been selected.
   */
  onConfirmDeletePress = async () => {
    const {confirmingDeleteId} = this.s
    if (!confirmingDeleteId) {
      throw new Error("Expected a user role id before deletion")
    }
    const roleToDelete = this.s.userRoles.find((userRole) => String(userRole.id()) === confirmingDeleteId)
    if (!roleToDelete) {
      throw new Error(`User role not found: ${confirmingDeleteId}`)
    }
    try {
      await roleToDelete.destroy()
      this.s.userRoles = this.s.userRoles.filter(
        (userRole) => String(userRole.id()) !== confirmingDeleteId
      )
      this.s.confirmingDeleteId = null
      this.s.status = {
        kind: "success",
        text: _("The user role was deleted")
      }
    } catch (error) {
      this.s.confirmingDeleteId = null
      this.s.status = {
        kind: "error",
        text: errorMessage(error)
      }
    }
  }
}

/** @type {NamedExoticComponent<UserRolesIndexScreenProps>} */
const UserRolesIndexScreenComponent = memo(shapeComponent(UserRolesIndexScreen))

export default UserRolesIndexScreenComponent
